<template>
  <div>
    <div class="p-6">
      <h1 class="text-2xl font-bold mb-4">Gestione Cliente</h1>

      <!-- Customer Info Card -->
      <div class="bg-white rounded-lg shadow p-6 mb-6">
        <h2 class="text-xl font-semibold mb-4">Informazioni Cliente</h2>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <p class="text-sm text-gray-600">Nome Cliente</p>
            <p class="font-medium">{{ customer.name }}</p>
          </div>
          <div>
            <p class="text-sm text-gray-600">Email</p>
            <p class="font-medium">{{ customer.email }}</p>
          </div>
          <div>
            <p class="text-sm text-gray-600">Utenti Creati</p>
            <p class="font-medium">{{ customer.users_count }}</p>
          </div>
          <div>
            <p class="text-sm text-gray-600">Sessioni Attive</p>
            <p :class="customer.active_sessions >= customer.max_sessions ? 'text-red-600 font-bold' : 'text-green-600 font-medium'">
              {{ customer.active_sessions }}
            </p>
          </div>
          
        </div>

        <!-- Max Sessions Section -->
        <div class="mt-6 pt-6 border-t border-gray-200">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm text-gray-600">Limite Sessioni Concorrenti</p>
              <p class="font-medium text-lg">{{ customer.max_sessions }}</p>
            </div>
            <div>
              <p class="text-sm text-gray-600">Google Maps Key</p>
              <p class="font-medium">
                {{ customer.maps_key || 'Non configurata' }}
              </p>
            </div>
            <button
              @click="openEditModal"
              class="bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600"
            >
              Modifica Impostazioni
            </button>
            <button
              @click="openEditBridgeModal"
              class="bg-orange-500 text-white px-4 py-2 rounded-md hover:bg-orange-600"
            >
              Bridge
            </button>
          </div>
        </div>
      </div>

      <div class="bg-white rounded-lg shadow mb-6 mt-6 overflow-hidden">
        <div class="border-b border-gray-100">
          <v-tabs
            v-model="activeTab"
            color="primary"
            align-tabs="start"
            density="compact"
          >
            <v-tab :value="0" class="text-none font-semibold">Gestione Moduli</v-tab>
            <v-tab :value="1" class="text-none font-semibold">Configurazioni Moduli</v-tab>
            <v-tab :value="2" class="text-none font-semibold">Configurazione Utenti</v-tab>
            <v-tab :value="3" class="text-none font-semibold">API Keys</v-tab>

          </v-tabs>
        </div>
        <v-window v-model="activeTab">
          <!-- Tab 1: Module Management -->
          <v-window-item :value="0">
            <div class="p-6">
              <h2 class="text-xl font-semibold mb-4">Gestione Moduli</h2>

              <!-- Available Modules to Add -->
              <div class="mb-6">
                <h3 class="text-lg font-medium mb-3">Aggiungi Modulo</h3>
                <div class="flex items-center gap-3">
                  <v-select
                    v-model="selectedModuleToAdd"
                    :options="availableModulesToAdd"
                    label="name"
                    :reduce="module => module.id"
                    placeholder="Seleziona un modulo"
                    class="flex-1"
                  />
                  <button
                    @click="associateModule"
                    :disabled="!selectedModuleToAdd || moduleForm.processing"
                    class="bg-green-500 text-white px-4 py-2 rounded-md hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Aggiungi
                  </button>
                </div>
              </div>
              
              <!-- Associated Modules -->
              <div>
                <h3 class="text-lg font-medium mb-3">Moduli Associati</h3>
                <div v-if="customerModules.length === 0" class="text-gray-500 text-sm">
                  Nessun modulo associato
                </div>
                <div v-else class="space-y-4">
                  <div
                    v-for="module in customerModules"
                    :key="module.id"
                    class="border border-gray-200 rounded-lg p-4 bg-gray-50"
                  >
                    <div class="flex items-center justify-between">
                      <div class="flex items-center gap-3">
                        <span class="font-medium">{{ module.name }}</span>
                        <span v-if="module.description" class="text-sm text-gray-600">{{ module.description }}</span>
                      </div>
                      <button
                        @click="disassociateModule(module.id)"
                        class="text-red-600 hover:text-red-800 text-sm font-medium"
                        :disabled="moduleForm.processing"
                      >
                        Rimuovi
                      </button>
                    </div>

                    <!-- Submodules Table -->
                    <div v-if="module.submodules && module.submodules.length > 0" class="mt-3">
                      <div class="bg-white rounded border border-gray-200">
                        <table class="min-w-full divide-y divide-gray-200">
                          <thead class="bg-gray-50">
                            <tr>
                              <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Gruppo sottomoduli</th>
                              <th class="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Attivo</th>
                            </tr>
                          </thead>
                          <tbody class="bg-white divide-y divide-gray-200">
                            <tr v-for="submoduleGroup in module.submodules" :key="module.id + '-' + submoduleGroup.group">
                              <td class="px-4 py-2 text-sm text-gray-600">
                                <div class="flex flex-wrap gap-1">
                                  <span
                                    v-for="item in submoduleGroup.list"
                                    class="inline-block px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs"
                                  >
                                    {{ $t(item) }}
                                  </span>
                                </div>
                              </td>
                              <td class="px-4 py-2">
                                <div class="flex justify-center">
                                  <v-switch
                                    :model-value="submoduleGroup.active"
                                    @update:model-value="toggleSubmoduleGroup(module.id, submoduleGroup.group, $event)"
                                    color="primary"
                                    hide-details
                                    density="compact"
                                  />
                                </div>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </v-window-item>  
          <v-window-item :value="1">
            <div class="p-6 pb-12">
              <h2 class="text-xl font-semibold mb-4">Configurazioni Moduli</h2>
              
              <div v-if="Object.keys(modulesConfig).length === 0" class="text-gray-500 text-center py-8">
                Nessuna configurazione disponibile. Associa moduli per visualizzare le configurazioni.
              </div>

              <div v-else class="space-y-6">
                <div
                  v-for="(moduleData, moduleSlug) in modulesConfig"
                  :key="moduleSlug"
                  :ref="el => { if (el) moduleRefs[moduleSlug] = el }"
                  class="border border-gray-200 rounded-lg p-4 bg-gray-50"
                >
                  <h3 :class="['text-lg font-medium mb-4', moduleHasErrors(moduleSlug) ? 'text-red-600' : '']">{{ moduleData.name }}</h3>
                  
                  <div v-if="Object.keys(moduleData.parameters).length === 0" class="text-gray-500 text-sm">
                    Nessun parametro configurabile per questo modulo
                  </div>

                  <div v-else>
                    <v-expansion-panels v-model="expandedPanels[moduleSlug]" variant="accordion" multiple>
                      <v-expansion-panel
                          v-for="(params, groupKey) in getGroupedParameters(moduleSlug)"
                          :key="groupKey"
                          class="border-b last:border-0"
                      >
                        <v-expansion-panel-title class="font-bold py-3">
                            {{ getGroupTitle(groupKey, moduleSlug) }}
                            <template v-slot:actions="{ expanded }">
                              <i :class="expanded ? 'fas fa-chevron-up' : 'fas fa-chevron-down'" class="text-blue-500"></i>
                            </template>
                        </v-expansion-panel-title>

                        <v-expansion-panel-text>
                          <div class="space-y-6">
                            <div
                                v-for="paramData in params"
                                :key="paramData.key"
                                class="flex flex-col gap-2 pb-4 border-b border-gray-50 last:border-0 last:pb-0"
                            >
                              <label class="text-base font-semibold">
                                  - {{ paramData.label }}
                              </label>

                              <label class="text-sm font-medium text-gray-700">
                                  {{ paramData.description }}
                              </label>

                              <!-- Boolean Toggle -->
                              <v-switch
                                  v-if="isBoolean(paramData.options)"
                                  :model-value="getFieldValue(moduleSlug, paramData.key)"
                                  @update:model-value="updateField(moduleSlug, paramData.key, $event)"
                                  :label="getFieldValue(moduleSlug, paramData.key) ? 'Attivo' : 'Non attivo'"
                                  color="primary"
                                  hide-details
                              />

                              <!-- Select dropdown -->
                              <div v-else-if="isSelect(paramData.options)">
                                  <v-select
                                      :model-value="getFieldValue(moduleSlug, paramData.key)"
                                      @update:model-value="updateField(moduleSlug, paramData.key, $event)"
                                      :options="getSelectOptions(paramData.options)"
                                      :reduce="option => typeof option === 'object' && option !== null ? option.value : option"
                                      placeholder="Seleziona"
                                  />
                                  <div v-if="getFieldError(moduleSlug, paramData.key)" class="text-xs text-red-600 mt-1">
                                      {{ getFieldError(moduleSlug, paramData.key) }}
                                  </div>
                              </div>

                              <!-- Multi-select dropdown -->
                              <div v-else-if="isMultiselect(paramData.options)">
                                  <v-select
                                      :model-value="getFieldValue(moduleSlug, paramData.key) || []"
                                      @update:model-value="updateField(moduleSlug, paramData.key, $event)"
                                      :options="getSelectOptions(paramData.options.items)"
                                      :reduce="option => typeof option === 'object' && option !== null ? option.value : option"
                                      multiple
                                      placeholder="Seleziona uno o più listini"
                                  />
                              </div>

                              
                              <!-- JSON key-value editor -->
                              <div v-else-if="isJsonMapping(paramData.options)">
                                  <table class="w-full text-sm border rounded mt-2">
                                      <thead>
                                          <tr class="bg-gray-100 text-left">
                                              <th class="px-3 py-2 font-medium">Deposito</th>
                                              <th class="px-3 py-2 font-medium">Causale/i</th>
                                              <th class="px-3 py-2 w-10"></th>
                                          </tr>
                                      </thead>
                                      <tbody>
                                          <tr v-for="(row, idx) in getJsonRows(moduleSlug, paramData.key)" :key="idx" class="border-t">
                                              <td class="px-2 py-1">
                                                  <v-select
                                                      :model-value="row.key"
                                                      @update:model-value="updateJsonRowKey(moduleSlug, paramData.key, idx, $event)"
                                                      :options="getSelectOptions(paramData.options.keys)"
                                                      :reduce="option => typeof option === 'object' && option !== null ? option.value : option"
                                                      placeholder="Seleziona deposito"
                                                      density="compact"
                                                      variant="outlined"
                                                      hide-details
                                                  />
                                              </td>
                                              <td class="px-2 py-1">
                                                  <v-select
                                                      :model-value="row.display ? row.display.split(', ') : []"
                                                      @update:model-value="updateJsonRowValue(moduleSlug, paramData.key, idx, Array.isArray($event) ? $event.join(', ') : $event)"
                                                      :options="getSelectOptions(paramData.options.values)"
                                                      :reduce="option => typeof option === 'object' && option !== null ? option.value : option"
                                                      multiple
                                                      placeholder="Seleziona causale/i"
                                                      density="compact"
                                                      variant="outlined"
                                                      hide-details
                                                  />
                                              </td>
                                              <td class="px-2 py-1 text-center">
                                                  <button @click="removeJsonRow(moduleSlug, paramData.key, idx)" class="text-red-500 hover:text-red-700" title="Rimuovi">
                                                      <i class="fa fa-trash"></i>
                                                  </button>
                                              </td>
                                          </tr>
                                      </tbody>
                                  </table>
                                  <button @click="addJsonRow(moduleSlug, paramData.key)" class="mt-2 text-sm text-blue-600 hover:text-blue-800">
                                      <i class="fa fa-plus mr-1"></i> Aggiungi riga
                                  </button>
                              </div>

                              <!-- Courier Mapping editor -->
                              <div v-else-if="isCourierMapping(paramData.options)">
                                  <table class="w-full text-sm border rounded mt-2">
                                      <thead>
                                          <tr class="bg-gray-100 text-left">
                                              <th class="px-3 py-2 font-medium">Slug</th>
                                              <th class="px-3 py-2 font-medium">Nome</th>
                                              <th class="px-3 py-2 font-medium">Email</th>
                                              <th class="px-3 py-2 font-medium">Telefono</th>
                                              <th class="px-3 py-2 w-10"></th>
                                          </tr>
                                      </thead>
                                      <tbody>
                                          <tr v-for="(row, idx) in getCourierRows(moduleSlug, paramData.key)" :key="idx" class="border-t">
                                              <td class="px-2 py-1">
                                                  <v-text-field
                                                      v-model="row.slug"
                                                      @update:model-value="syncCourierToForm(moduleSlug, paramData.key)"
                                                      placeholder="slug"
                                                      density="compact"
                                                      variant="outlined"
                                                      hide-details
                                                  />
                                              </td>
                                              <td class="px-2 py-1">
                                                  <v-text-field
                                                      v-model="row.name"
                                                      @update:model-value="syncCourierToForm(moduleSlug, paramData.key)"
                                                      placeholder="Nome"
                                                      density="compact"
                                                      variant="outlined"
                                                      hide-details
                                                  />
                                              </td>
                                              <td class="px-2 py-1">
                                                  <v-text-field
                                                      v-model="row.recipient_email"
                                                      @update:model-value="syncCourierToForm(moduleSlug, paramData.key)"
                                                      placeholder="Email"
                                                      density="compact"
                                                      variant="outlined"
                                                      hide-details
                                                  />
                                              </td>
                                              <td class="px-2 py-1">
                                                  <v-text-field
                                                      v-model="row.recipient_phone"
                                                      @update:model-value="syncCourierToForm(moduleSlug, paramData.key)"
                                                      placeholder="Telefono"
                                                      density="compact"
                                                      variant="outlined"
                                                      hide-details
                                                  />
                                              </td>
                                              <td class="px-2 py-1 text-center">
                                                  <button @click="removeCourierRow(moduleSlug, paramData.key, idx)" class="text-red-500 hover:text-red-700" title="Rimuovi">
                                                      <i class="fa fa-trash"></i>
                                                  </button>
                                              </td>
                                          </tr>
                                      </tbody>
                                  </table>
                                  <button @click="addCourierRow(moduleSlug, paramData.key)" class="mt-2 text-sm text-blue-600 hover:text-blue-800">
                                      <i class="fa fa-plus mr-1"></i> Aggiungi azienda
                                  </button>
                              </div>

                              <!-- Print composition editor (ordered list) -->
                              <div v-else-if="isPrintComposition(paramData.options)">
                                  <div class="mb-2 flex items-center gap-2" style="max-width: 260px;">
                                      <span class="text-sm font-medium text-gray-700 whitespace-nowrap">Stato documento:</span>
                                      <v-select
                                          :model-value="getPrintCompositionStatus(moduleSlug, paramData.key)"
                                          @update:model-value="setPrintCompositionStatus(moduleSlug, paramData.key, $event)"
                                          :options="printCompositionStatusOptions"
                                          label="title"
                                          :reduce="option => option.value"
                                          :clearable="false"
                                          class="flex-1"
                                      />
                                  </div>
                                  <table class="w-full text-sm border rounded mt-2">
                                      <thead>
                                          <tr class="bg-gray-100 text-left">
                                              <th class="px-3 py-2 w-16">Ordine</th>
                                              <th class="px-3 py-2 font-medium">Tipo</th>
                                              <th class="px-3 py-2 font-medium">URL PDF</th>
                                              <th class="px-3 py-2 w-10"></th>
                                          </tr>
                                      </thead>
                                      <tbody>
                                          <tr v-for="(row, idx) in getPrintCompositionRows(moduleSlug, paramData.key)" :key="idx" class="border-t">
                                              <td class="px-2 py-1 whitespace-nowrap">
                                                  <button @click="movePrintCompositionRow(moduleSlug, paramData.key, idx, -1)" :disabled="idx === 0" class="text-gray-500 hover:text-gray-800 disabled:opacity-25" title="Sposta su">
                                                      <i class="fa fa-arrow-up"></i>
                                                  </button>
                                                  <button @click="movePrintCompositionRow(moduleSlug, paramData.key, idx, 1)" :disabled="idx === getPrintCompositionRows(moduleSlug, paramData.key).length - 1" class="text-gray-500 hover:text-gray-800 disabled:opacity-25 ml-1" title="Sposta giù">
                                                      <i class="fa fa-arrow-down"></i>
                                                  </button>
                                              </td>
                                              <td class="px-2 py-1">
                                                  <span v-if="row.type === 'portal_print'" class="font-medium">Stampa Portale</span>
                                                  <span v-else>URL PDF</span>
                                              </td>
                                              <td class="px-2 py-1">
                                                  <v-text-field
                                                      v-if="row.type === 'url'"
                                                      :model-value="row.value"
                                                      @update:model-value="updatePrintCompositionRow(moduleSlug, paramData.key, idx, 'value', $event)"
                                                      placeholder="https://.../documento.pdf"
                                                      density="compact"
                                                      variant="outlined"
                                                      hide-details
                                                  />
                                              </td>
                                              <td class="px-2 py-1 text-center">
                                                  <button v-if="row.type !== 'portal_print'" @click="removePrintCompositionRow(moduleSlug, paramData.key, idx)" class="text-red-500 hover:text-red-700" title="Rimuovi">
                                                      <i class="fa fa-trash"></i>
                                                  </button>
                                              </td>
                                          </tr>
                                      </tbody>
                                  </table>
                                  <button @click="addPrintCompositionRow(moduleSlug, paramData.key)" class="mt-2 text-sm text-blue-600 hover:text-blue-800">
                                      <i class="fa fa-plus mr-1"></i> Aggiungi PDF da URL
                                  </button>
                              </div>

                              <!-- Text input -->
                              <div v-else>
                                  <v-text-field
                                      :model-value="getFieldValue(moduleSlug, paramData.key)"
                                      @update:model-value="updateField(moduleSlug, paramData.key, $event)"
                                      variant="outlined"
                                      density="compact"
                                      :error-messages="getFieldError(moduleSlug, paramData.key)"
                                      :disabled="isFieldDisabled(moduleSlug, paramData.key)"
                                  />
                                  <div v-if="isFieldDisabled(moduleSlug, paramData.key)" class="text-xs text-amber-600 mt-1">
                                      <i class="fa fa-warning"></i> {{ getDisabledReason(moduleSlug, paramData.key) }}
                                  </div>
                                  <div v-else-if="paramData.validation" class="text-xs text-gray-500 mt-1">
                                      {{ getValidationHint(paramData.validation) }}
                                  </div>
                              </div>

                            </div>
                          </div>
                        </v-expansion-panel-text>
                      </v-expansion-panel>
                    </v-expansion-panels>
                  </div>

                </div>
              </div>

              <!-- Save Button -->
              <div v-if="Object.keys(modulesConfig).length > 0" class="fixed bottom-0 left-0 right-0 p-4 bg-white border-t border-gray-200 z-50 flex justify-center shadow-lg">
                <PrimaryButton
                  @click="saveModuleConfigurations"
                  :class="{ 'opacity-25': configForm.processing }"
                  :disabled="configForm.processing"
                >
                  Salva Tutte le Configurazioni
                </PrimaryButton>
              </div>
            </div>
          </v-window-item>
          <v-window-item :value="2">
            <div class="p-6">
              <h2 class="text-xl font-semibold mb-4">Configurazione Utenti</h2>
              <div v-if="customerUsers.length === 0" class="text-gray-500 text-center py-8">
                Nessun utente associato a questo cliente.
              </div>
              <div v-else class="space-y-6">
                <div
                  v-for="user in customerUsers"
                  :key="user.id"
                  class="border border-gray-200 rounded-lg p-4 bg-gray-50"
                >
                  <div class="mb-4">
                    <p class="font-medium">{{ user.name }}</p>
                    <p class="text-sm text-gray-500">{{ user.email }}</p>
                  </div>
                  <div
                    v-for="(moduleData, moduleSlug) in userModulesConfig"
                    :key="moduleSlug"
                    class="mb-4 border-t pt-3"
                  >
                    <p class="text-sm font-semibold text-gray-600 mb-3">{{ moduleData.name }}</p>
                    <div class="space-y-3">
                      <div
                        v-for="(paramDef, paramKey) in moduleData.parameters"
                        :key="paramKey"
                      >
                        <p class="text-sm font-medium text-gray-700 mb-1">{{ paramDef.label }}</p>
                        <p v-if="paramDef.description" class="text-xs text-gray-500 mb-1">{{ paramDef.description }}</p>
                        <v-switch
                          v-if="isBoolean(paramDef.options)"
                          v-model="userParamConfigs[user.id][moduleSlug][paramKey]"
                          color="primary"
                          hide-details
                        />
                        <v-select
                          v-else-if="isSelect(paramDef.options)"
                          v-model="userParamConfigs[user.id][moduleSlug][paramKey]"
                          :options="getSelectOptions(paramDef.options)"
                          :reduce="option => typeof option === 'object' && option !== null ? option.value : option"
                          placeholder="Nessun valore"
                          :clearable="true"
                        />
                        <div v-else-if="isMultiselect(paramDef.options)">
                          <v-select
                            v-model="userParamConfigs[user.id][moduleSlug][paramKey]"
                            :options="getSelectOptions(paramDef.options.items)"
                            :reduce="option => typeof option === 'object' && option !== null ? option.value : option"
                            multiple
                            placeholder="Nessun valore"
                            :clearable="true"
                          />
                        </div>
                        <div v-else-if="isUserStorageMapping(paramDef.options)">
                          <table class="w-full text-sm border rounded mt-2">
                            <thead>
                              <tr class="bg-gray-100 text-left">
                                <th class="px-3 py-2 font-medium">Deposito</th>
                                <th class="px-3 py-2 font-medium">Tipo Specificatore</th>
                                <th class="px-3 py-2 font-medium">Tipo</th>
                                <th class="px-3 py-2 w-10"></th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr v-for="(row, idx) in (userParamConfigs[user.id][moduleSlug][paramKey] || [])" :key="idx" class="border-t">
                                <td class="px-2 py-1">
                                  <v-select
                                    :model-value="row.storage"
                                    @update:model-value="row.storage = $event"
                                    :options="getSelectOptions(paramDef.options.storages)"
                                    :reduce="option => typeof option === 'object' && option !== null ? option.value : option"
                                    placeholder="Seleziona deposito"
                                    :clearable="false"
                                  />
                                </td>
                                <td class="px-2 py-1">
                                  <v-select
                                    :model-value="row.specificator_type"
                                    @update:model-value="row.specificator_type = $event"
                                    :options="paramDef.options.specificatorTypes"
                                    :reduce="option => option.value"
                                    placeholder="Tipo specificatore"
                                    :clearable="false"
                                  />
                                </td>
                                <td class="px-2 py-1">
                                  <v-select
                                    :model-value="row.tipo"
                                    @update:model-value="row.tipo = $event"
                                    :options="[{label: 'Default', value: 'default'}, {label: 'Obbligatorio', value: 'obbligatorio'}, {label: 'Bloccato', value: 'bloccato'}]"
                                    :reduce="option => option.value"
                                    placeholder="Tipo"
                                    :clearable="false"
                                  />
                                </td>
                                <td class="px-2 py-1 text-center">
                                  <button @click="removeUserStorageRow(user.id, moduleSlug, paramKey, idx)" class="text-red-500 hover:text-red-700" title="Rimuovi">
                                    <i class="fa fa-trash"></i>
                                  </button>
                                </td>
                              </tr>
                            </tbody>
                          </table>
                          <button @click="addUserStorageRow(user.id, moduleSlug, paramKey)" class="mt-2 text-sm text-blue-600 hover:text-blue-800">
                            <i class="fa fa-plus mr-1"></i> Aggiungi deposito
                          </button>
                        </div>
                        <v-text-field
                          v-else-if="paramDef.options"
                          v-model="userParamConfigs[user.id][moduleSlug][paramKey]"
                          variant="outlined"
                          density="compact"
                        />
                      </div>
                    </div>
                  </div>
                  <div v-if="Object.keys(userModulesConfig).length === 0" class="text-sm text-gray-400 text-center py-4">
                    Nessun parametro utente configurabile.
                  </div>
                  <div v-else class="mt-4 flex justify-end border-t pt-3">
                    <PrimaryButton
                      @click="saveAllUserModuleConfigs(user.id)"
                      :disabled="savingUserModule === user.id"
                    >Salva</PrimaryButton>
                  </div>
                </div>
              </div>
            </div>
          </v-window-item>
          <v-window-item :value="3">
            <div class="p-4 space-y-6">
              <div>
                <h3 class="text-lg font-semibold">API Keys</h3>
                <p class="text-sm text-gray-500">Le chiavi frontend sono visibili agli utenti del browser e devono essere limitate per dominio e API.</p>
              </div>
              <div
                v-for="category in apiKeyCategories"
                :key="category.value"
                class="border border-gray-200 rounded-lg p-4"
              >
                <div class="flex items-center justify-between mb-3">
                  <h4 class="font-semibold">{{ category.label }}</h4>
                  <button type="button" @click.stop.prevent="addApiKey(category.value)" class="text-sm text-blue-600 hover:text-blue-800">
                    <i class="fa fa-plus mr-1"></i> Aggiungi chiave
                  </button>
                </div>
                <div v-if="apiKeysForm.api_keys[category.value].length === 0" class="text-sm text-gray-500">
                  Nessuna chiave configurata.
                </div>
                <div v-else class="space-y-4">
                  <div
                    v-for="(apiKey, index) in apiKeysForm.api_keys[category.value]"
                    :key="apiKey.id"
                    class="rounded border border-gray-200 p-3"
                  >
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <v-text-field
                        v-model="apiKey.provider"
                        label="Provider"
                        variant="outlined"
                        density="compact"
                        hide-details="auto"
                      />
                      <v-text-field
                        v-model="apiKey.key"
                        label="API Key"
                        type="password"
                        autocomplete="new-password"
                        :hint="apiKey.execution === 'backend' ? (apiKey.has_key ? 'Chiave configurata. Lascia vuoto per mantenerla.' : 'La chiave viene utilizzata solo dal server.') : ''"
                        :persistent-hint="apiKey.execution === 'backend' && apiKey.has_key"
                        variant="outlined"
                        density="compact"
                        hide-details="auto"
                      />
                      <v-text-field
                        v-model="apiKey.account"
                        label="Account associato"
                        variant="outlined"
                        density="compact"
                        hide-details="auto"
                      />
                      <v-select
                        v-model="apiKey.execution"
                        :options="executionOptions"
                        label="label"
                        :reduce="option => option.value"
                        :clearable="false"
                        placeholder="Dove viene usata"
                      />
                    </div>
                    <div class="mt-2 flex flex-wrap items-center gap-x-6 gap-y-2">
                      <v-switch
                        v-model="apiKey.enabled"
                        :label="apiKey.enabled ? 'Attiva' : 'Disattiva'"
                        color="primary"
                        hide-details
                        density="compact"
                      />
                      <v-switch
                        v-model="apiKey.supports_truck"
                        label="Supporta veicoli pesanti"
                        color="primary"
                        hide-details
                        density="compact"
                      />
                      <button
                        type="button"
                        @click.stop.prevent="removeApiKey(category.value, index)"
                        class="ml-auto text-red-500 hover:text-red-700"
                        title="Rimuovi chiave"
                      >
                        <i class="fa fa-trash"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              <div v-if="Object.keys(apiKeysForm.errors).length" class="text-sm text-red-600">
                {{ Object.values(apiKeysForm.errors).join(' ') }}
              </div>
              <div class="flex justify-end border-t pt-3">
                <PrimaryButton
                  @click="saveApiKeys"
                  :disabled="apiKeysForm.processing"
                >Salva API Keys</PrimaryButton>
              </div>
            </div>
          </v-window-item>


        </v-window>
      </div>
    </div>

    <!-- Modal per modificare limite sessioni -->
    <DialogModal :show="showEditModal" @close="closeEditModal">
      <template #title>
        Modifica configurazioni cliente
      </template>

      <template #content>
        <div class="text-gray-600">
          <p class="font-medium mb-4">Cliente: {{ customer.name }}</p>
          <div class="mb-4">
            <v-text-field
              v-model.number="editForm.max_sessions"
              type="number"
              min="1"
              label="Numero massimo di sessioni concorrenti"
              variant="outlined"
              density="compact"
            />
          </div>
          <div class="mb-4">
            <v-text-field
              v-model="editForm.maps_key"
              label="Google Maps Key"
              variant="outlined"
              density="compact"
              maxlength="50"
            />
          </div>
          <div class="mb-4">
            <v-text-field
              v-model="editForm.slug_override"
              label="Slug aziendale (override)"
              variant="outlined"
              density="compact"
              maxlength="100"
              :hint="editForm.slug_override ? '' : 'Se vuoto, viene usato lo slug generato dal nome cliente'"
              persistent-hint
            />
          </div>
          <div v-if="editForm.errors.maps_key" class="text-red-600 text-sm">
            {{ editForm.errors.max_sessions }}
          </div>
        </div>
      </template>

      <template #footer>
        <SecondaryButton @click="closeEditModal" :disabled="editForm.processing">
          Annulla
        </SecondaryButton>

        <PrimaryButton 
          class="ms-3" 
          @click="updateMaxSessionsAndGmapsKey"
          :class="{ 'opacity-25': editForm.processing }" 
          :disabled="editForm.processing"
        >
          Salva
        </PrimaryButton>
      </template>
    </DialogModal>

    <!-- Modal per modificare configurazione Bridge -->
    <DialogModal :show="showEditBridgeModal" @close="closeEditBridgeModal">
      <template #title>
        Modifica configurazione Bridge
      </template>

      <template #content>
        <div class="text-gray-600">
          <p class="font-medium mb-4">Cliente: {{ customer.name }}</p>
          <div class="mb-4">
            <label class="text-sm font-medium text-gray-700">
              Abilita Bridge
            </label>
            <v-switch
              v-model="editBridgeForm.bridge"
              color="primary"
              hide-details
            />
          </div>
          <div class="mb-4">
            <v-text-field
              v-model="editBridgeForm.bridge_url"
              label="URL Bridge"
              variant="outlined"
              density="compact"
              maxlength="50"
            />
          </div>
          <div class="mb-4">
            <v-text-field
              v-model="editBridgeForm.bridge_token"
              label="Token Bridge"
              variant="outlined"
              density="compact"
              maxlength="50"
            />
          </div>
          <div class="mb-4">
            <label class="text-sm font-medium text-gray-700">
              Azienda Magoweb
            </label>
            <v-switch
              v-model="editBridgeForm.magoweb_company"
              color="primary"
              hide-details
            />
          </div>
          <div class="flex items-center gap-4 flex-wrap">
            <div>
              Test Bridge:
              <span v-if="bridgeTest" class="text-green-600 font-medium"><i class="fas fa-check-circle"></i> Connessione riuscita</span>
              <span v-else class="text-red-600 font-medium"><i class="fas fa-times-circle"></i> Connessione fallita</span>
            </div>
            <button
              @click="testGetData"
              :disabled="getDataTestLoading"
              class="bg-purple-500 text-white px-4 py-2 rounded-md hover:bg-purple-600 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
            >
              <i class="fas fa-wand-magic-sparkles mr-1"></i>
              {{ getDataTestLoading ? 'In corso...' : 'Test Magic Link' }}
            </button>
            <button
              @click="testGetDataTable"
              :disabled="getDataTableTestLoading"
              class="bg-teal-500 text-white px-4 py-2 rounded-md hover:bg-teal-600 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
            >
              <i class="fas fa-table mr-1"></i>
              {{ getDataTableTestLoading ? 'In corso...' : 'Test MagoApi' }}
            </button>
          </div>
          <div v-if="getDataTestResult !== null" class="mt-3">
            <pre class="bg-purple-50 rounded p-3 text-xs overflow-auto max-h-60 whitespace-pre-wrap break-all">{{ JSON.stringify(getDataTestResult, null, 2) }}</pre>
          </div>
          <div v-if="getDataTableTestResult !== null" class="mt-3">
            <pre class="bg-teal-50 rounded p-3 text-xs overflow-auto max-h-60 whitespace-pre-wrap break-all">{{ JSON.stringify(getDataTableTestResult, null, 2) }}</pre>
          </div>
          <div v-if="editBridgeForm.errors.bridge_url" class="text-red-600 text-sm">
            {{ editBridgeForm.errors.bridge_url }}
          </div>
        </div>
      </template>

      <template #footer>
        <SecondaryButton @click="closeEditBridgeModal" :disabled="editBridgeForm.processing">
          Annulla
        </SecondaryButton>

        <PrimaryButton 
          class="ms-3" 
          @click="updateBridgeConfiguration"
          :class="{ 'opacity-25': editBridgeForm.processing }" 
          :disabled="editBridgeForm.processing || (editBridgeForm.bridge && (!editBridgeForm.bridge_url || !editBridgeForm.bridge_token))"
        >
          Salva
        </PrimaryButton>
      </template>
    </DialogModal>

    <!-- Toast Notification -->
    <ToastNotification
      :show="toast.show"
      :message="toast.message"
      :type="toast.type"
      @update:show="toast.show = $event"
    />
  </div>
</template>

<script>
import { defineComponent } from 'vue';
import { useForm } from '@inertiajs/vue3';
import DialogModal from '@/Components/DialogModal.vue';
import PrimaryButton from '@/Components/PrimaryButton.vue';
import SecondaryButton from '@/Components/SecondaryButton.vue';
import ToastNotification from '@/Components/ToastNotification.vue';
import { VExpansionPanels, VExpansionPanel, VExpansionPanelTitle, VExpansionPanelText, VWindow, VTabs, VWindowItem, VTab } from 'vuetify/components';




export default defineComponent({
  components: {
    DialogModal,
    PrimaryButton,
    SecondaryButton,
    ToastNotification,
    VExpansionPanels,
    VExpansionPanel,
    VExpansionPanelTitle,
    VExpansionPanelText,
    VWindow,
    VTabs,
    VWindowItem,
    VTab
  },
  props: {
    customer: {
      type: Object,
      required: true,
    },
    availableModules: {
      type: Array,
      required: true,
    },
    customerModules: {
      type: Array,
      required: true,
    },
    modulesConfig: {
      type: Object,
      required: true,
    },

    configuration: {
      type: Object,
      required: true,
    },
    bridgeTest : {
      type: Boolean,
      required: false,
      default: false,
    },
    customerUsers: {
      type: Array,
      default: () => [],
    },
    userModulesConfig: {
      type: Object,
      default: () => ({}) 
    },

  },
  data() {
    return {
      activeTab: 0,
      showEditModal: false,
      showEditBridgeModal: false,
      selectedModuleToAdd: '',
      editForm: useForm({
        max_sessions: 1,
        maps_key: this.customer.maps_key || '',
        slug_override: this.configuration?.slug_override || '',
      }),
      editBridgeForm: useForm({
        bridge: false,
        bridge_url: "",
        bridge_token: "",
        magoweb_company: false,
      }),
      apiKeysForm: useForm({
        api_keys: {
          routing: [],
          general: [],
        },
      }),
      apiKeyCategories: [
        { value: 'routing', label: 'Routing' },
        { value: 'general', label: 'Generali' },
      ],
      executionOptions: [
        { value: 'frontend', label: 'Frontend' },
        { value: 'backend', label: 'Backend' },
      ],
      moduleForm: useForm({
        module_id: null,
      }),
      configForm: useForm({
        modules: {},
      }),
      toast: {
        show: false,
        message: '',
        type: 'success'
      },
      jsonEditorData: {},
      printCompositionStatus: {},
      printCompositionStatusOptions: [
        { value: 'order', title: 'Ordine' },
        { value: 'offert', title: 'Offerta' },
        { value: 'draft', title: 'Proposta' },
      ],
      userParamConfigs: {},    
      savingUserModule: null,
      expandedPanels: {},
      moduleRefs: {},
      getDataTestLoading: false,
      getDataTestResult: null,
      getDataTableTestLoading: false,
      getDataTableTestResult: null,

    };
  },
  created() {
    this.initializeConfigForm();
    this.initUserParamConfigs();
    this.initializeApiKeys();
  },
  computed: {
    availableModulesToAdd() {
      const customerModuleIds = this.customerModules.map(m => m.id);
      return this.availableModules.filter(m => !customerModuleIds.includes(m.id));
    },
  },
  methods: {
    initializeApiKeys() {
      const existingApiKeys = this.customer.api_keys && typeof this.customer.api_keys === 'object' && !Array.isArray(this.customer.api_keys)
        ? this.customer.api_keys
        : {};

      this.apiKeyCategories.forEach(({ value: category }) => {
        const entries = Array.isArray(existingApiKeys[category]) ? existingApiKeys[category] : [];
        this.apiKeysForm.api_keys[category] = entries.map(apiKey => ({
          id: apiKey.id || this.createApiKeyId(),
          provider: apiKey.provider || '',
          key: apiKey.key || '',
          has_key: Boolean(apiKey.has_key || (apiKey.execution === 'backend' && apiKey.key)),
          account: apiKey.account || '',
          enabled: apiKey.enabled ?? true,
          supports_truck: apiKey.supports_truck ?? false,
          execution: apiKey.execution === 'backend' ? 'backend' : (apiKey.execution === 'frontend' ? 'frontend' : (category === 'routing' ? 'frontend' : 'backend')),
        }));
      });
    },
    createApiKeyId() {
      return globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    },
    addApiKey(category) {
      this.apiKeysForm.clearErrors();
      this.apiKeysForm.api_keys[category].push({
        id: this.createApiKeyId(),
        provider: '',
        key: '',
        has_key: false,
        account: '',
        enabled: true,
        supports_truck: false,
        execution: category === 'routing' ? 'frontend' : 'backend',
      });
    },
    removeApiKey(category, index) {
      this.apiKeysForm.clearErrors();
      this.apiKeysForm.api_keys[category].splice(index, 1);
    },
    saveApiKeys() {
      this.apiKeysForm.post(route('modules.core.aggiorna-api-keys'), {
        preserveScroll: true,
        onSuccess: () => {
          this.apiKeyCategories.forEach(({ value: category }) => {
            this.apiKeysForm.api_keys[category].forEach(apiKey => {
              if (apiKey.execution === 'backend') {
                apiKey.key = '';
                apiKey.has_key = true;
              }
            });
          });
          this.showToast('API Keys aggiornate con successo!', 'success');
        },
        onError: errors => {
          const firstError = Object.values(errors)[0];
          this.showToast(firstError || 'Errore durante il salvataggio delle API Keys', 'error');
        },
      });
    },
    initializeConfigForm() {
      const modules = {};
      
      Object.keys(this.modulesConfig).forEach(moduleSlug => {
        modules[moduleSlug] = {};
        if (!this.expandedPanels[moduleSlug]) {
          this.expandedPanels[moduleSlug] = [];
        }
        const moduleData = this.modulesConfig[moduleSlug];
        
        Object.keys(moduleData.parameters).forEach(paramKey => {
          modules[moduleSlug][paramKey] = moduleData.parameters[paramKey].value;
        });
      });
      
      this.configForm.modules = modules;
    },
    isBoolean(options) {
      return Array.isArray(options) && 
             options.length === 2 && 
             options[0] === false && 
             options[1] === true;
    },
    isSelect(options) {
      return Array.isArray(options) && 
             options.length > 0 && 
             !this.isBoolean(options);
    },
    isMultiselect(options) {
      return typeof options === 'object' && options !== null && !Array.isArray(options) && options.type === 'multiselect';
    },
    isJson(options) {
      return options === 'json';
    },
    isJsonMapping(options) {
      return typeof options === 'object' && options !== null && !Array.isArray(options) && options.type === 'json_mapping';
    },
    isCourierMapping(options) {
      return typeof options === 'object' && options !== null && !Array.isArray(options) && options.type === 'courier_mapping';
    },
    isUserStorageMapping(options) {
      return typeof options === 'object' && options !== null && !Array.isArray(options) && options.type === 'user_storage_mapping';
    },
    isPrintComposition(options) {
      return typeof options === 'object' && options !== null && !Array.isArray(options) && options.type === 'print_composition';
    },
    getJsonRows(moduleSlug, paramKey) {
      const k = `${moduleSlug}.${paramKey}`;
      if (!this.jsonEditorData[k]) {
        const raw = this.getFieldValue(moduleSlug, paramKey);
        let obj = {};
        if (raw && typeof raw === 'string') {
          try { obj = JSON.parse(raw); } catch (e) { obj = {}; }
        } else if (raw && typeof raw === 'object') {
          obj = raw;
        }
        this.jsonEditorData[k] = Object.entries(obj).map(([key, val]) => ({
          key,
          display: Array.isArray(val) ? val.join(', ') : String(val),
        }));
      }
      return this.jsonEditorData[k];
    },
    syncJsonToForm(moduleSlug, paramKey) {
      const k = `${moduleSlug}.${paramKey}`;
      const rows = this.jsonEditorData[k] || [];
      const obj = {};
      rows.forEach(r => {
        if (!r.key.trim()) return;
        const parts = r.display.split(',').map(s => s.trim()).filter(Boolean);
        obj[r.key.trim()] = parts.length === 1 ? parts[0] : (parts.length === 0 ? '' : parts);
      });
      this.updateField(moduleSlug, paramKey, JSON.stringify(obj, null, 2));
    },
    updateJsonRowKey(moduleSlug, paramKey, idx, newKey) {
      const k = `${moduleSlug}.${paramKey}`;
      this.jsonEditorData[k][idx].key = newKey;
      this.syncJsonToForm(moduleSlug, paramKey);
    },
    updateJsonRowValue(moduleSlug, paramKey, idx, newDisplay) {
      const k = `${moduleSlug}.${paramKey}`;
      this.jsonEditorData[k][idx].display = newDisplay;
      this.syncJsonToForm(moduleSlug, paramKey);
    },
    addJsonRow(moduleSlug, paramKey) {
      const k = `${moduleSlug}.${paramKey}`;
      if (!this.jsonEditorData[k]) this.jsonEditorData[k] = [];
      this.jsonEditorData[k].push({ key: '', display: '' });
    },
    removeJsonRow(moduleSlug, paramKey, idx) {
      const k = `${moduleSlug}.${paramKey}`;
      this.jsonEditorData[k].splice(idx, 1);
      this.syncJsonToForm(moduleSlug, paramKey);
    },
    getCourierRows(moduleSlug, paramKey) {
      const k = `courier.${moduleSlug}.${paramKey}`;
      if (!this.jsonEditorData[k]) {
        const raw = this.getFieldValue(moduleSlug, paramKey);
        let obj = {};
        if (raw && typeof raw === 'string') {
          try { obj = JSON.parse(raw); } catch (e) { obj = {}; }
        } else if (raw && typeof raw === 'object') {
          obj = raw;
        }
        this.jsonEditorData[k] = Object.entries(obj).map(([slug, data]) => ({
          slug,
          name: data.name || '',
          recipient_email: data.recipient_email || '',
          recipient_phone: data.recipient_phone || '',
        }));
      }
      return this.jsonEditorData[k];
    },
    syncCourierToForm(moduleSlug, paramKey) {
      const k = `courier.${moduleSlug}.${paramKey}`;
      const rows = this.jsonEditorData[k] || [];
      const obj = {};
      rows.forEach(r => {
        if (!r.slug.trim()) return;
        obj[r.slug.trim()] = {
          name: r.name,
          recipient_email: r.recipient_email,
          recipient_phone: r.recipient_phone,
        };
      });
      this.updateField(moduleSlug, paramKey, JSON.stringify(obj, null, 2));
    },
    addCourierRow(moduleSlug, paramKey) {
      const k = `courier.${moduleSlug}.${paramKey}`;
      if (!this.jsonEditorData[k]) this.jsonEditorData[k] = [];
      this.jsonEditorData[k].push({ slug: '', name: '', recipient_email: '', recipient_phone: '' });
    },
    removeCourierRow(moduleSlug, paramKey, idx) {
      const k = `courier.${moduleSlug}.${paramKey}`;
      this.jsonEditorData[k].splice(idx, 1);
      this.syncCourierToForm(moduleSlug, paramKey);
    },
    getPrintCompositionData(moduleSlug, paramKey) {
      const k = `printcomp.${moduleSlug}.${paramKey}`;
      if (!this.jsonEditorData[k]) {
        const raw = this.getFieldValue(moduleSlug, paramKey);
        let obj = {};
        if (raw && typeof raw === 'string') {
          try { obj = JSON.parse(raw); } catch (e) { obj = {}; }
        } else if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
          obj = raw;
        }
        if (!obj || typeof obj !== 'object' || Array.isArray(obj)) obj = {};
        this.printCompositionStatusOptions.forEach(({ value: status }) => {
          let arr = Array.isArray(obj[status]) ? obj[status] : [];
          if (!arr.some(r => r.type === 'portal_print')) {
            arr = [{ type: 'portal_print' }, ...arr];
          }
          obj[status] = arr.map(r => ({ type: r.type, value: r.value || '' }));
        });
        this.jsonEditorData[k] = obj;
      }
      return this.jsonEditorData[k];
    },
    getPrintCompositionStatus(moduleSlug, paramKey) {
      const k = `${moduleSlug}.${paramKey}`;
      if (!this.printCompositionStatus[k]) this.printCompositionStatus[k] = 'order';
      return this.printCompositionStatus[k];
    },
    setPrintCompositionStatus(moduleSlug, paramKey, status) {
      const k = `${moduleSlug}.${paramKey}`;
      this.printCompositionStatus[k] = status;
    },
    getPrintCompositionRows(moduleSlug, paramKey) {
      const data = this.getPrintCompositionData(moduleSlug, paramKey);
      const status = this.getPrintCompositionStatus(moduleSlug, paramKey);
      return data[status];
    },
    syncPrintCompositionToForm(moduleSlug, paramKey) {
      const data = this.getPrintCompositionData(moduleSlug, paramKey);
      const out = {};
      this.printCompositionStatusOptions.forEach(({ value: status }) => {
        out[status] = (data[status] || []).map(r => r.type === 'portal_print' ? { type: 'portal_print' } : { type: r.type, value: r.value || '' });
      });
      this.updateField(moduleSlug, paramKey, JSON.stringify(out, null, 2));
    },
    updatePrintCompositionRow(moduleSlug, paramKey, idx, field, value) {
      const rows = this.getPrintCompositionRows(moduleSlug, paramKey);
      rows[idx][field] = value;
      this.syncPrintCompositionToForm(moduleSlug, paramKey);
    },
    addPrintCompositionRow(moduleSlug, paramKey) {
      const rows = this.getPrintCompositionRows(moduleSlug, paramKey);
      rows.push({ type: 'url', value: '' });
      this.syncPrintCompositionToForm(moduleSlug, paramKey);
    },
    removePrintCompositionRow(moduleSlug, paramKey, idx) {
      const rows = this.getPrintCompositionRows(moduleSlug, paramKey);
      rows.splice(idx, 1);
      this.syncPrintCompositionToForm(moduleSlug, paramKey);
    },
    movePrintCompositionRow(moduleSlug, paramKey, idx, direction) {
      const rows = this.getPrintCompositionRows(moduleSlug, paramKey);
      const newIdx = idx + direction;
      if (newIdx < 0 || newIdx >= rows.length) return;
      const [row] = rows.splice(idx, 1);
      rows.splice(newIdx, 0, row);
      this.syncPrintCompositionToForm(moduleSlug, paramKey);
    },
    getJsonError(moduleSlug, paramKey) {
      const value = this.getFieldValue(moduleSlug, paramKey);
      if (!value || (typeof value === 'string' && value.trim() === '')) return '';
      if (typeof value === 'object') return '';
      try { JSON.parse(value); return ''; } catch (e) { return 'JSON non valido'; }
    },
    getOptionKey(option) {
      if (typeof option === 'object' && option !== null) {
        return option.id || option.Storage || option.Payment || option.Reason || JSON.stringify(option);
      }
      return option;
    },
    getOptionValue(option) {
      if (typeof option === 'object' && option !== null) {
        return option.id || option.Storage || option.Payment || option.Reason || option.Operation || option.WC || option.value || '';
      }
      return option;
    },
    getOptionLabel(option) {
      if (typeof option === 'object' && option !== null) {
        const value = this.getOptionValue(option);
        const name = option.name || option.Description || '';
        return name ? `${value} - ${name}` : value;
      }
      return option;
    },
    getSelectOptions(options) {
      return options.map(option => {
        if (typeof option === 'object' && option !== null) {
          return {
            label: this.getOptionLabel(option),
            value: this.getOptionValue(option),
          };
        }
        return option;
      });
    },
    getFieldValue(moduleSlug, paramKey) {
      if (!this.configForm.modules[moduleSlug]) {
        return '';
      }
      return this.configForm.modules[moduleSlug][paramKey];
    },
    updateField(moduleSlug, paramKey, value) {
      if (!this.configForm.modules[moduleSlug]) {
        this.configForm.modules[moduleSlug] = {};
      }
      this.configForm.modules[moduleSlug][paramKey] = value;
      
      // Handle cross-field dependencies
      if (moduleSlug === 'gestione-magazzino') {
        // If changing year format to 4-digit, clear the prefix
        if (paramKey === 'lot_year_format' && value === '4-digit') {
          this.configForm.modules[moduleSlug]['lot_prefix'] = '';
        }
      }
    },
    getFieldError(moduleSlug, paramKey) {
      const errorKey = `modules.${moduleSlug}.${paramKey}`;
      return this.configForm.errors[errorKey] || '';
    },
    getValidationHint(validationRules) {
      if (!validationRules) return '';
      
      // Parse validation rules and create user-friendly hints
      const hints = [];
      
      if (validationRules.includes('max:')) {
        const max = validationRules.match(/max:(\d+)/);
        if (max) {
          hints.push(`Massimo ${max[1]} caratteri`);
        }
      }
      
      if (validationRules.includes('regex:/^[a-zA-Z0-9]*$/')) {
        hints.push('Solo lettere e numeri');
      }

      if (validationRules.includes('regex:/^[0-9]*$/')) {
        hints.push('Solo numeri');
      }
      
      return hints.join(' • ');
    },
    isFieldDisabled(moduleSlug, paramKey) {
      // Check cross-field dependencies
      // For gestione-magazzino module
      if (moduleSlug === 'gestione-magazzino') {
        // Disable lot_prefix if lot_year_format is 4-digit
        if (paramKey === 'lot_prefix') {
          const yearFormat = this.getFieldValue(moduleSlug, 'lot_year_format');
          return yearFormat === '4-digit';
        }
      }
      
      return false;
    },
    getDisabledReason(moduleSlug, paramKey) {
      // Provide user-friendly explanation for why field is disabled
      if (moduleSlug === 'gestione-magazzino') {
        if (paramKey === 'lot_prefix') {
          return 'Il prefisso non è disponibile quando si utilizza il formato anno a 4 cifre';
        }
      }
      
      return 'Campo non disponibile con la configurazione corrente';
    },
    moduleHasErrors(moduleSlug) {
      return Object.keys(this.configForm.errors).some(
        key => key.startsWith(`modules.${moduleSlug}.`)
      );
    },
    getGroupsWithErrors(moduleSlug) {
      const groupedParams = this.getGroupedParameters(moduleSlug);
      const groupKeys = Object.keys(groupedParams);
      const errorIndices = [];
      groupKeys.forEach((groupKey, idx) => {
        const params = groupedParams[groupKey];
        const hasError = params.some(paramData => this.getFieldError(moduleSlug, paramData.key));
        if (hasError) errorIndices.push(idx);
      });
      return errorIndices;
    },
    handleSaveErrors() {
      let firstErrorSlug = null;
      Object.keys(this.modulesConfig).forEach(moduleSlug => {
        if (this.moduleHasErrors(moduleSlug)) {
          if (!firstErrorSlug) firstErrorSlug = moduleSlug;
          this.expandedPanels[moduleSlug] = this.getGroupsWithErrors(moduleSlug);
        }
      });
      if (firstErrorSlug) {
        this.activeTab = 1;
        this.$nextTick(() => {
          const el = this.moduleRefs[firstErrorSlug];
          if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        });
      }
    },
    associateModule() {
      if (!this.selectedModuleToAdd) return;
      
      this.moduleForm.module_id = this.selectedModuleToAdd;
      this.moduleForm.post(route('modules.core.associa-modulo'), {
        preserveScroll: true,
        onSuccess: () => {
          this.selectedModuleToAdd = '';
          this.showToast('Modulo associato con successo!', 'success');
        },
      });
    },
    disassociateModule(moduleId) {
      if (!confirm('Sei sicuro di voler rimuovere questo modulo?')) return;
      
      this.moduleForm.module_id = moduleId;
      this.moduleForm.post(route('modules.core.disassocia-modulo'), {
        preserveScroll: true,
        onSuccess: () => {
          this.showToast('Modulo rimosso con successo!', 'success');
        },
      });
    },
    openEditModal() {
      this.editForm.max_sessions = this.customer.max_sessions;
      this.editForm.maps_key = this.customer.maps_key;
      this.editForm.slug_override = this.configuration?.slug_override || '';
      this.showEditModal = true;
    },
    openEditBridgeModal() {
      this.editBridgeForm.bridge = this.configuration.bridge ?? false;
      this.editBridgeForm.bridge_url = this.configuration.bridge_url || "";
      this.editBridgeForm.bridge_token = this.configuration.bridge_token || "";
      this.editBridgeForm.magoweb_company = this.configuration.magoweb_company ?? false;
      this.showEditBridgeModal = true;
    },
    closeEditModal() {
      this.showEditModal = false;
      this.editForm.reset();
    },
    closeEditBridgeModal() {
      this.showEditBridgeModal = false;
      this.editBridgeForm.reset();
      this.getDataTestResult = null;
      this.getDataTableTestResult = null;
    },
    async testGetData() {
      this.getDataTestLoading = true;
      this.getDataTestResult = null;
      try {
        const response = await axios.post(route('modules.core.test-magiclink-getdata'));
        this.getDataTestResult = response.data;
      } catch (e) {
        this.getDataTestResult = { success: false, error: e.message };
      } finally {
        this.getDataTestLoading = false;
      }
    },
    async testGetDataTable() {
      this.getDataTableTestLoading = true;
      this.getDataTableTestResult = null;
      try {
        const response = await axios.post(route('modules.core.test-magoapi-getdatatable'));
        this.getDataTableTestResult = response.data;
      } catch (e) {
        this.getDataTableTestResult = { success: false, error: e.message };
      } finally {
        this.getDataTableTestLoading = false;
      }
    },
    updateBridgeConfiguration() {
      console.log(this.editBridgeForm.data());
      this.editBridgeForm.post(route('modules.core.aggiorna-bridge'), {
        preserveScroll: true,
        onSuccess: () => {
          this.closeEditBridgeModal();
          this.showToast('Configurazione Bridge aggiornata con successo!', 'success');
        },
      });
    },
    updateMaxSessionsAndGmapsKey() {
      console.log(this.editForm.data());
      this.editForm.post(route('modules.core.aggiorna-limite-gmapskey'), {
        preserveScroll: true,
        onSuccess: () => {
          this.closeEditModal();
          this.showToast('Impostazioni aggiornate con successo!', 'success');
        },
      });
    },
    saveModuleConfigurations() {
      const modules = {};
      let hasJsonError = false;
      
      Object.keys(this.modulesConfig).forEach(moduleSlug => {
        modules[moduleSlug] = {};
        const moduleData = this.modulesConfig[moduleSlug];
        
        Object.keys(moduleData.parameters).forEach(paramKey => {
          const paramData = moduleData.parameters[paramKey];
          if (this.isJson(paramData.options) || this.isJsonMapping(paramData.options) || this.isCourierMapping(paramData.options) || this.isPrintComposition(paramData.options)) {
            const val = this.getFieldValue(moduleSlug, paramKey);
            if (val && typeof val === 'string' && val.trim() !== '') {
                try {
                    JSON.parse(val);
                } catch (e) {
                    hasJsonError = true;
                }
            }
          }
          modules[moduleSlug][paramKey] = this.getFieldValue(moduleSlug, paramKey);
        });
      });
      if (hasJsonError) return;
      this.configForm.modules = modules;
      this.configForm.post(route('modules.core.aggiorna-configurazioni-moduli'), {
        preserveScroll: true,
        onSuccess: () => {
          this.jsonEditorData = {};
          this.initializeConfigForm();
          this.showToast('Configurazioni salvate con successo!', 'success');
        },
        onError: () => {
          this.handleSaveErrors();
        },
      });
    },
    showToast(message, type = 'success') {
      this.toast.message = message;
      this.toast.type = type;
      this.toast.show = true;
    },
    toggleSubmoduleGroup(moduleId, groupId, isActive) {
      const form = useForm({
        module_id: moduleId,
        submodule_group: groupId,
        active: isActive,
      });

      form.post(route('modules.core.toggle-gruppo-sottomoduli'), {
        preserveScroll: true,
        onSuccess: () => {
          this.showToast(
            isActive ? 'Gruppo sottomoduli attivato!' : 'Gruppo sottomoduli disattivato!',
            'success'
          );
        },
        onError: () => {
          this.showToast('Errore durante l\'aggiornamento', 'error');
        },
      });
    },

    getGroupedParameters(moduleSlug) {
      const parameters = this.modulesConfig[moduleSlug].parameters;
      const groups = {};

      Object.entries(parameters).forEach(([paramKey, paramData]) => {
          // Usa 'generale' come fallback se submodule non è definito
          const groupKey = paramData.submodule || 'generale';
          
          if (!groups[groupKey]) {
              groups[groupKey] = [];
          }
          
          groups[groupKey].push({
              key: paramKey,
              ...paramData
          });
      });

      // Ordiniamo i gruppi per mettere 'generale' in cima se esiste
      const sortedGroups = {};
      if (groups['generale']) {
          sortedGroups['generale'] = groups['generale'];
          delete groups['generale'];
      }
      
      return { ...sortedGroups, ...groups };
    },

    // Metodo helper per ottenere il titolo del gruppo (opzionale)
    getGroupTitle(groupKey, moduleSlug) {
      if (groupKey === 'generale') return 'GENERALI';
      
      // Prova a cercare il nome del sottomodulo nei moduli associati
      const customerModule = this.customerModules.find(m => m.slug === moduleSlug);
      if (customerModule && customerModule.submodules) {
          // I sottomoduli sono raggruppati per 'group' nel template riga 101, 
          // ma qui cerchiamo per ID sottomodulo se possibile o traduciamo la chiave
          return this.$t('modules.gm.' + groupKey) || groupKey.replace(/-/g, ' ').toUpperCase();
      }
      
      return groupKey.replace(/-/g, ' ').toUpperCase();
    },

    initUserParamConfigs() {
      const configs = {};
      this.customerUsers.forEach(user => {
        configs[user.id] = {};
        Object.keys(this.userModulesConfig).forEach(moduleSlug => {
          configs[user.id][moduleSlug] = {};
          Object.keys(this.userModulesConfig[moduleSlug].parameters).forEach(paramKey => {
            configs[user.id][moduleSlug][paramKey] = user.params?.[moduleSlug]?.[paramKey] ?? null;
          });
        });
      });
      this.userParamConfigs = configs;
    },

    
    addUserStorageRow(userId, moduleSlug, paramKey) {
      if (!Array.isArray(this.userParamConfigs[userId][moduleSlug][paramKey])) {
        this.userParamConfigs[userId][moduleSlug][paramKey] = [];
      }
      this.userParamConfigs[userId][moduleSlug][paramKey].push({ storage: '', specificator_type: 6750211, tipo: 'default' });
    },
    removeUserStorageRow(userId, moduleSlug, paramKey, idx) {
      this.userParamConfigs[userId][moduleSlug][paramKey].splice(idx, 1);
    },

    saveAllUserModuleConfigs(userId) {
      this.savingUserModule = userId;
      const form = useForm({
        guid:    this.customer.id,
        modules: this.userParamConfigs[userId] ?? {},
      });
      form.post(route('modules.core.utente.module-config', { id: userId }), {
        preserveScroll: true,
        onSuccess: () => {
          this.showToast('Configurazione salvata con successo', 'success');
          this.savingUserModule = null;
        },
        onError: () => {
          this.showToast('Errore nel salvataggio', 'error');
          this.savingUserModule = null;
        },
      });
    },

  },
  watch: {
    modulesConfig: {
        handler() {
            this.jsonEditorData = {};
            this.initializeConfigForm();
        },
        deep: true,
    },
  },
});
</script>
